// src/features/plan/PlanCompletenessCell.jsx
//
// PLACES-MARKETS-DRAWER (amendment #2, Corey 2026-10-31): 4-dot market plan
// completeness for the Markets Plan column. The dots are the four persisted
// market-plan fields — Strategy, Target date, Owner, Note. The former fifth
// "all offices planned" gate is dropped here; Portfolio Command's separate
// office-based Plan Coverage metric is unaffected.
//
// The same pure counter (countMarketPlanFields) drives the drawer's
// "n of 4 set" and the Markets "Plans complete" KPI so all three agree.

import React from "react";
import { T } from "../../app/tokens.js";

const DOT_SIZE = 7;
const DOT_GAP  = 4;
export const PLAN_FIELD_TOTAL = 4;
export const PLAN_FIELD_LABELS = ["Strategy", "Target date", "Owner", "Note"];

function isSet(val) {
  return val !== null && val !== undefined && String(val).trim() !== "";
}

/** Pure: which of the four persisted market-plan fields are set. */
export function marketPlanFieldFlags(market = {}) {
  return [
    isSet(market.strategy),
    isSet(market.strategyTargetDate ?? market.target_date),
    isSet(market.owner),
    isSet(market.planNote ?? market.plan_note),
  ];
}

/** Pure: count of set fields (0-4). */
export function countMarketPlanFields(market) {
  return marketPlanFieldFlags(market).filter(Boolean).length;
}

/** Pure: plans complete across a market population (all four set). */
export function countCompletePlans(markets = []) {
  return markets.filter((m) => countMarketPlanFields(m) === PLAN_FIELD_TOTAL).length;
}

export function PlanCompletenessCell({
  strategy, strategyTargetDate, target_date, owner, planNote, plan_note, isCompact = false,
}) {
  const flags = marketPlanFieldFlags({ strategy, strategyTargetDate, target_date, owner, planNote, plan_note });
  const count = flags.filter(Boolean).length;

  let statusLabel = `${count} of ${PLAN_FIELD_TOTAL}`;
  let statusColor = T.light;
  if (count === 0) { statusLabel = "No plan set"; statusColor = T.red; }
  else if (count === PLAN_FIELD_TOTAL) statusColor = T.green;

  const tooltip = PLAN_FIELD_LABELS.map((l, i) => `${i + 1}. ${l}: ${flags[i] ? "set" : "not set"}`).join("\n");

  return (
    <div data-testid="plan-completeness-cell" data-count={count}
      style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <div data-testid="plan-completeness-dots" title={tooltip} aria-label={`Plan ${count} of ${PLAN_FIELD_TOTAL} set`}
        style={{ display: "flex", gap: DOT_GAP, alignItems: "center" }}>
        {flags.map((on, i) => (
          <div key={i} data-testid={`plan-dot-${i}`} data-set={on ? "true" : "false"}
            style={{
              width: DOT_SIZE, height: DOT_SIZE, borderRadius: "50%", flexShrink: 0,
              background: on ? (count === PLAN_FIELD_TOTAL ? T.green : T.planCompFill) : T.border,
            }} />
        ))}
      </div>
      {!isCompact && (
        <span data-testid="plan-completeness-label"
          style={{ fontSize: T.fsXxxs, fontWeight: T.fwSemibold, color: statusColor, whiteSpace: "nowrap", lineHeight: 1 }}>
          {statusLabel}
        </span>
      )}
    </div>
  );
}
